// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {PRODUCT_API_NAME} from '@app/features/app/config/I18nDisplayConstants';
import type {DeveloperApplication} from '@app/features/devtools/models/DeveloperApplication';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import styles from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsTab.module.css';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import * as SnowflakeUtils from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans} from '@lingui/react/macro';
import {AppWindowIcon, CaretRightIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface ApplicationsListProps {
	applications: ReadonlyArray<DeveloperApplication>;
	onSelectApplication: (appId: string) => void;
}
export const ApplicationsList: React.FC<ApplicationsListProps> = observer(({applications, onSelectApplication}) => {
	if (applications.length === 0) {
		return (
			<div className={styles.emptyState}>
				<StatusSlate
					Icon={AppWindowIcon}
					title={<Trans>No applications yet</Trans>}
					description={<Trans>Create your first application to get started with the {PRODUCT_API_NAME}.</Trans>}
				/>
			</div>
		);
	}
	return (
		<div className={styles.listContainer}>
			{applications.map((app) => {
				const avatarUrl = app.bot
					? AvatarUtils.getUserAvatarURL({id: app.bot.id, avatar: app.bot.avatar}, false)
					: null;
				const createdAt = DateUtils.getFormattedShortDate(SnowflakeUtils.extractTimestamp(app.id));
				return (
					<div key={app.id} className={styles.itemContainer}>
						<FocusRing offset={-2}>
							<button type="button" className={styles.itemButton} onClick={() => onSelectApplication(app.id)}>
								<div className={styles.itemLeft}>
									{avatarUrl ? (
										<div className={styles.itemAvatar} style={{backgroundImage: `url(${avatarUrl})`}} aria-hidden />
									) : (
										<div className={styles.itemAvatarPlaceholder} aria-hidden>
											{app.name.charAt(0).toUpperCase()}
										</div>
									)}
									<div className={styles.itemTextBlock}>
										<div className={styles.itemTitleRow}>
											<span className={styles.itemName}>{app.name}</span>
										</div>
										<div className={styles.itemMetaRow}>
											<span>
												<Trans>Created {createdAt}</Trans>
											</span>
										</div>
									</div>
								</div>
								<CaretRightIcon className={styles.itemChevron} weight="bold" />
							</button>
						</FocusRing>
					</div>
				);
			})}
		</div>
	);
});
